import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import './ProjectStatePill.scss';
import {
  getProjectStatusIcon,
  useProjectsStatuses,
  getProjectStatus,
} from '../../common/hooks/useProjectsStatuses';

import {
  isProjectCreationInProgress,
  isProjectActive,
  isProjectArchived,
  isProjectInactive,
  isProjectArchiveComplete,
  isProjectActivateComplete,
  isProjectArchivingInProgress,
  isProjectInactivePendingInProgress,
  isProjectReactivePendingInProgress,
  isProjectUnArchivingInProgress,
  isErrorStatus,
} from '../../common/helpers/projectStatusesUtils';

const ProjectStatePill = ({
  pillText,
  showWhenActive,
  isSmall,
  projectId,
  showPendingStatus,
  header,
  isFavourite = false,
  isSearch = false,
}) => {
  const { t } = useTranslation();
  const projectsStates = useProjectsStatuses({ isFavourite, isSearch });
  const projectStateIcon = getProjectStatusIcon(projectsStates, projectId);
  const projectStatus = getProjectStatus(projectsStates, projectId);
  const iconSize = 13;

  if (!projectStatus || (!showWhenActive && isProjectActive(projectStatus))) {
    return null;
  }

  return (
    <div
      className="project-state-pill-container"
      data-testid="project-state-pill"
    >
      <div
        className={classNames('project-state-pill', {
          'project-state-pill__no-icon': isProjectActive(projectStatus),
          'project-state-pill__small': isSmall,
        })}
      >
        {(isProjectInactive(projectStatus) ||
          isProjectArchived(projectStatus)) && (
          <Icon
            name={projectStateIcon}
            className="fill icon-black"
            width={iconSize}
            height={iconSize}
          />
        )}

        {isProjectActivateComplete(projectStatus) && (
          <span data-testid="project-state-pill-status">
            {t('po.navtopbar.selectactive')}
          </span>
        )}
        {isProjectReactivePendingInProgress(projectStatus) && (
          <span>Active, Status pending</span>
        )}

        {isErrorStatus(projectStatus) && <span>Inactive, Status pending</span>}

        {isProjectInactive(projectStatus) && (
          <span
            /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
            tabIndex={0}
            data-testid="project-state-pill-status"
            className={`${header ? 'hide-text-header-xs' : ''}`}
          >
            {`${t('po.navtopbar.selectinactive')}${
              isProjectInactivePendingInProgress(projectStatus) &&
              showPendingStatus
                ? ', pending'
                : ''
            }`}
          </span>
        )}
        {isProjectArchiveComplete(projectStatus) && (
          <span
            className={`${header ? 'hide-text-header-xs' : ''}`}
            data-testid="project-state-pill-status"
          >
            {t('po.navtopbar.selectallarchived')}
          </span>
        )}
        {isProjectArchivingInProgress(projectStatus) && (
          <span data-testid="project-state-pill-status-pending">
            Archived, status pending
          </span>
        )}
        {isProjectUnArchivingInProgress(projectStatus) && (
          <span>Unarchived, status pending</span>
        )}
        {isProjectCreationInProgress(projectStatus) && <span>{pillText}</span>}
      </div>
    </div>
  );
};

ProjectStatePill.propTypes = {
  projectId: PropTypes.number.isRequired,
  showWhenActive: PropTypes.bool,
  showPendingStatus: PropTypes.bool,
  isSmall: PropTypes.bool,
  header: PropTypes.bool,
  isFavourite: PropTypes.bool,
  isSearch: PropTypes.bool,
};

ProjectStatePill.defaultProps = {
  showWhenActive: true,
  showPendingStatus: false,
  isSmall: false,
  header: false,
};

export default ProjectStatePill;
